import React, { useContext, useLayoutEffect, useReducer } from 'react';
import { createGlobalStyle } from 'styled-components';
import './App.css';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ToastHost } from './toasts';
import MessageBox from './components/MessageBox';
import SignInScreen from './screens/SignInScreen';
import ResetPassword from './screens/ResetPassword';
import SignupScreen from './screens/SignUpScreen.jsx';
import ProfileScreen from './screens/ProfileScreen';
import AdminScreen3 from './screens/AdminUsers';
import AdminFooter from './components/AdminFooter';
import ScrollToTop from './components/ScrollToTop';
import ChangePassword from './screens/ChangePassword';
import VerifyUser from './screens/VerifyUser';
import Topbar from './components/Topbar/Topbar';
import AdminRoute from './components/AdminRoute';
import AuthRoute from './components/AuthRoute';
import AccountDetail from './screens/AccountDetail/AccountDetail';
import { getSettingsRoutes } from './screens/Settings/settingsRoutes';
import ContractTemplateSetup from './screens/ContractTemplates/ContractTemplateSetup';
import ContractTemplateEditor from './screens/ContractTemplates/ContractTemplateEditor';
import DetailPageLoader from './components/DetailPageLoader/DetailPageLoader';
import AddUser from './screens/Users/NewUser';
import EditUser from './screens/Users/EditUser';
import AddContact from './screens/Contacts/NewContact';
import EditContact from './screens/Contacts/EditContact';
import ContactList from './screens/Contacts/ContactList';
import ContactDetail from './screens/ContactDetail/ContactDetail';
import AddAccount from './screens/Accounts/NewAccount';
import EditAccount from './screens/Accounts/EditAccount';
import AccountList from './screens/Accounts/AccountList';
import DebtDetails from './screens/DebtDetail/DebtDetails';
import DebtList from './screens/Debts/DebtList';
import PendingCountDues from './screens/Debts/PendingCountDues';
import AddDebt from './screens/Debts/NewDebt';
import EditDebt from './screens/Debts/EditDebt';
import CreditDetails from './screens/CreditDetail/CreditDetails';
import CreditList from './screens/Credits/CreditList';
import AddCredit from './screens/Credits/NewCredit';
import EditCredit from './screens/Credits/EditCredit';
import PaymentList from './screens/Payments/PaymentList';
import AddPayment from './screens/Payments/NewPayment';
import EditPayment from './screens/Payments/EditPayment';
import PaymentDetail from './screens/Payments/PaymentDetail';
import CreditPaymentList from './screens/CreditPayments/CreditPaymentList';
import AddCreditPayment from './screens/CreditPayments/NewCreditPayment';
import EditCreditPayment from './screens/CreditPayments/EditCreditPayment';
import CreditPaymentDetail from './screens/CreditPayments/CreditPaymentDetail';
import ContractDetail from './screens/ContractDetail/ContractDetail';
import ContractList from './screens/Contracts/ContractList';
import AddContract from './screens/Contracts/NewContract';
import EditContract from './screens/Contracts/EditContract';
import ContractPaymentEditor from './screens/Contracts/ContractPaymentEditor';
import InterestList from './screens/Interests/InterestList';
import AddInterest from './screens/Interests/NewInterest';
import EditInterest from './screens/Interests/EditInterest';
import InterestInfo from './screens/Interests/InterestInfo';
import AssetList from './screens/Assets/AssetList';
import ImportBills from './screens/Assets/ImportBills';
import AddAsset from './screens/Assets/NewAsset';
import EditAsset from './screens/Assets/EditAsset';
import AssetDetails from './screens/AssetDetail/AssetDetails';
import AddAssetSale from './screens/AssetSales/AddAssetSale.jsx';
import NewAssetSale from './screens/AssetSales/NewAssetSale.jsx';
import EditAssetSale from './screens/AssetSales/EditAssetSale.jsx';
import AssetSaleDetail from './screens/AssetSaleDetail/AssetSaleDetail.jsx';
import AssetSalesList from './screens/AssetSales/AssetSalesList';
import NewBankAccount from './screens/BankAccounts/NewBankAccount';
import BillsList from './screens/Bills/BillsList';
import AddBill from './screens/Bills/NewBills';
import EditBill from './screens/Bills/EditBills';
import EditBillTemplate from './screens/Bills/EditBillTemplate';
import BillDetail from './screens/Bills/BillDetail';
import ConfirmPayment from './screens/Bills/ConfirmPayment';
import NewInterestNew from './screens/Interests/NewInterestNew';
import NewPaymentNew from './screens/Payments/NewPaymentNew';
import EditDebtStartDate from './screens/Debts/EditDebtStartDate';
import DebtDuePayment from './screens/Debts/DebtDuePayment';
import EditCreditStartDate from './screens/Credits/EditCreditStartDate';
import QuickPayment from './screens/Payments/QuickPayment';
import NewBillsNew from './screens/Bills/NewBillsNew';
import Dashboard from './screens/Dashboard/Dashboard';
import NewCreditPaymentNew from './screens/CreditPayments/NewCreditPaymentNew';
import NewWarranty from './screens/Warranties/NewWarranty';
import NewDebtDue from './screens/DebtDues/NewDebtDue';
import Reports from './screens/Reports/Reports';
import Predictions from './screens/Predictions/Predictions';
import PrivacyPolicyScreen from './screens/PrivacyPolicyScreen';
import TermsOfServiceScreen from './screens/TermsOfServiceScreen';
import colorService from './services/color.service.js';
import { CustomBrowserRouter } from './CustomBrowserRouter.jsx';
import { history } from './history.js';
import { Store } from './Store';
import _settings from './services/settings.service.js';
import { isTermsOfServiceEnabled, isPrivacyPolicyEnabled } from './utils/featureFlags';

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true };
	case 'FETCH_SUCCESS':
		return { ...state, colors: action.payload.colors, companyName: action.payload.companyName, loading: false };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	default:
		return state;
	}
};

function InterestEditRedirect() {
	const { id } = useParams();
	return <Navigate to={`/AdminScreen/interests/editInterest/${id}`} replace />;
}

//setting theme colors
const GlobalStyles = createGlobalStyle`
:root {
  --primaryColor: ${props => props.colors.primaryColor};
  --secondaryColor: ${props => props.colors.secondaryColor};
  --background-color: ${props => props.colors.backgroundColor};
  --utilsColor:${props => props.colors.utilsColor};
  --navFontColor:${props => props.colors.navFontColor};
  --shadowNavColor:${props => props.colors.shadowNavColor};
  --IconsColor:${props => props.colors.iconsColor};
  --titlesColor:${props => props.colors.titlesColor};
  --btnFontColor:${props => props.colors.btnFontColor};
}
`;

function App() {
	const { dispatch: ctxDispatch, } = useContext(Store);
	const [{ loading, error, colors, companyName }, dispatch] = useReducer(reducer, {
		colors: {},
		companyName: '',
		loading: true,
		error: '',
	});

	//getting theme colors-to do: load before page



	const loadColors = async () => {
		const { data } = await colorService.list();
		return data;
	};
	const loadTabs = async () => {
		const {data} = await _settings.getTabs();
		return data;
	};
	const loadPublicConfig = async () => {
		const { data } = await _settings.listPublic();
		return data;
	};
	const initData = async () => {
		try {
			dispatch({ type: 'FETCH_REQUEST' });
			const [colors, tabs, publicConfig] = await Promise.all([loadColors(), loadTabs(), loadPublicConfig()]);
			ctxDispatch({ type: 'SET_TABS', payload: tabs });
			dispatch({ type: 'FETCH_SUCCESS', payload: { colors, companyName: publicConfig?.companyName } });
		} catch (error) {
			dispatch({ type: 'FETCH_FAIL', payload: error.message });
		}
	};

	useLayoutEffect(() => {
		initData();
	}, []);

	return (
		<>
			{' '}
			{loading ? (
				<DetailPageLoader entityLabel='app' />
			) : error ? (
				<MessageBox variant='danger'>{error}</MessageBox>
			) : (
				<>
					<Helmet defaultTitle={companyName || ''} titleTemplate={companyName ? `%s | ${companyName}` : '%s'} />
					<GlobalStyles colors={colors} />
					{/* <BrowserRouter> */}
					<CustomBrowserRouter history={history}>
						<ScrollToTop></ScrollToTop>
						<div className='d-flex flex-column site-container'>
							<Topbar companyName={companyName} />
							<main>
								<Routes>
									<Route path='/' element={<SignInScreen appName={companyName || 'Cuenta'} />}></Route>
									<Route path='/signin' element={<SignInScreen appName={companyName || 'Cuenta'} />}></Route>
									<Route
										path='/reset-password/'
										element={<ResetPassword />}
									></Route>
									<Route path='/signup' element={<SignupScreen />}></Route>
									<Route path='/privacy-policy' element={isPrivacyPolicyEnabled() ? <PrivacyPolicyScreen /> : <Navigate to='/signin' replace />}></Route>
									<Route path='/terms-of-service' element={isTermsOfServiceEnabled() ? <TermsOfServiceScreen /> : <Navigate to='/signin' replace />}></Route>
									<Route path='/profile' element={<ProfileScreen />}></Route>
									<Route
										path='/change-password/:token'
										element={<ChangePassword />}
									></Route>
									<Route
										path='/verify-user/:token'
										element={<VerifyUser />}
									></Route>
									<Route element={<AuthRoute />}>
										<Route path='/AdminScreen' element={<Dashboard />}></Route>
										<Route path='/AdminScreen/dashboard' element={<Dashboard />}></Route>
										<Route path='/AdminScreen/reports' element={<Reports />}></Route>
										<Route path='/AdminScreen/predictions' element={<Predictions />}></Route>
										<Route path='/AdminScreen/predictions/debtDues/search' element={<Predictions />}></Route>
										<Route path='/AdminScreen/predictions/creditDues/search' element={<Predictions />}></Route>
										<Route path='/AdminScreen/reports/debts/search' element={<Reports />}></Route>
										<Route path='/AdminScreen/reports/credits/search' element={<Reports />}></Route>
										<Route path='/AdminScreen/reports/contracts/search' element={<Reports />}></Route>
										<Route path='/AdminScreen/reports/debtDues/search' element={<Reports />}></Route>
										<Route path='/AdminScreen/reports/creditDues/search' element={<Reports />}></Route>
										<Route path='/AdminScreen/reports/accountsDetail/search' element={<Reports />}></Route>
										<Route path='/AdminScreen/reports/assets/search' element={<Reports />}></Route>
										<Route path='/AdminScreen/reports/assetSales/search' element={<Reports />}></Route>
										<Route path='/AdminScreen/reports/payments/search' element={<Reports />}></Route>
										<Route path='/AdminScreen/accounts' element={<AccountList />}></Route>
										<Route path='/AdminScreen/accounts/search' element={<AccountList />}></Route>
										<Route path='/AdminScreen/accounts/account/:id' element={<AccountDetail />}></Route>
										<Route path='/AdminScreen/newAccount' element={<AddAccount />}></Route>
										<Route path='/AdminScreen/newBankAccount' element={<NewBankAccount />}></Route>
										<Route path='/AdminScreen/accounts/editAccount/:id' element={<EditAccount />}></Route>
										<Route path='/AdminScreen/accounts/account/:id/newContact' element={<AddContact />}></Route>
										<Route path='/AdminScreen/accounts/account/:id/newCredit' element={<AddCredit />}></Route>
										<Route path='/AdminScreen/accounts/account/:id/newDebt' element={<AddDebt />}></Route>
										<Route path='/AdminScreen/accounts/account/:accountId/debts/:id/edit' element={<EditDebt />}></Route>
										<Route path='/AdminScreen/accounts/account/:id/newContract' element={<AddContract />}></Route>
										<Route path='/AdminScreen/accounts/account/:accountId/contracts/:id/edit' element={<EditContract />}></Route>
										<Route path='/AdminScreen/accounts/account/:id/newAsset' element={<AddAsset />}></Route>
										<Route path='/AdminScreen/accounts/account/:id/:tab' element={<AccountDetail />}></Route>
										<Route path='/AdminScreen/newUser' element={<AdminRoute><AddUser /></AdminRoute>}></Route>
										<Route path='/AdminScreen/users/editUser/:id' element={<AdminRoute><EditUser /></AdminRoute>}></Route>
										<Route path='/AdminScreen/users' element={<Navigate to='/AdminScreen/Config/usuarios' replace />}></Route>
										<Route path='/AdminScreen/newContact' element={<AddContact />}></Route>
										<Route path='/AdminScreen/contacts' element={<ContactList />}></Route>
										<Route path='/AdminScreen/contacts/editContact/:id' element={<EditContact />}></Route>
										<Route path='/AdminScreen/contacts/contact/:id' element={<ContactDetail />}></Route>
										<Route path='/AdminScreen/debts' element={<DebtList />}></Route>
										<Route path='/AdminScreen/debts/search' element={<DebtList />}></Route>
										<Route path='/AdminScreen/debts/pendingCounts' element={<PendingCountDues />}></Route>
										<Route path='/AdminScreen/debts/debt/:id' element={<DebtDetails />}></Route>
										<Route path='/AdminScreen/newDebt' element={<AddDebt />}></Route>
										<Route path='/AdminScreen/debts/editDebt/:id' element={<EditDebt />}></Route>
										<Route path='/AdminScreen/debts/debt/:id/newInterest' element={<NewInterestNew />}></Route>
										<Route path='/AdminScreen/debts/debt/:id/newPayment' element={<NewPaymentNew />}></Route>
										<Route path='/AdminScreen/debts/debt/:id/editStartDate' element={<EditDebtStartDate />}></Route>
										<Route path='/AdminScreen/debts/debt/:id/dues/:dueId/newPayment' element={<DebtDuePayment />}></Route>
										<Route path='/AdminScreen/debts/debt/:id/newDue' element={<NewDebtDue />}></Route>
										<Route path='/AdminScreen/debts/debt/:id/:tab' element={<DebtDetails />}></Route>
										<Route path='/AdminScreen/credits/credit/:id/newCreditPayment' element={<NewCreditPaymentNew />}></Route>
										<Route path='/AdminScreen/credits/credit/:id/newWarranty' element={<NewWarranty />}></Route>
										<Route path='/AdminScreen/credits/credit/:id/editStartDate' element={<EditCreditStartDate />}></Route>
										<Route path='/AdminScreen/credits' element={<CreditList />}></Route>
										<Route path='/AdminScreen/credits/search' element={<CreditList />}></Route>
										<Route path='/AdminScreen/credits/credit/:id' element={<CreditDetails />}></Route>
										<Route path='/AdminScreen/credits/credit/:id/:tab' element={<CreditDetails />}></Route>
										<Route path='/AdminScreen/newCredit' element={<AddCredit />}></Route>
										<Route path='/AdminScreen/credits/editCredit/:id' element={<EditCredit />}></Route>
										<Route path='/AdminScreen/creditPayments' element={<CreditPaymentList />}></Route>
										<Route path='/AdminScreen/newCreditPayment' element={<AddCreditPayment />}></Route>
										<Route path='/AdminScreen/creditPayments/editCreditPayment/:id' element={<EditCreditPayment />}></Route>
										<Route path='/AdminScreen/creditPayments/creditPayment/:id' element={<CreditPaymentDetail />}></Route>
										<Route path='/AdminScreen/payments' element={<PaymentList />}></Route>
										<Route path='/AdminScreen/newPayment' element={<AddPayment />}></Route>
										<Route path='/AdminScreen/quickPayment' element={<QuickPayment />}></Route>
										<Route path='/AdminScreen/payments/editPayment/:id' element={<EditPayment />}></Route>
										<Route path='/AdminScreen/payments/payment/:id' element={<PaymentDetail />}></Route>
										<Route path='/AdminScreen/bills' element={<BillsList />}></Route>
										<Route path='/AdminScreen/newBill' element={<AddBill />}></Route>
										<Route path='/AdminScreen/bills/editBill/:id' element={<EditBill />}></Route>
										<Route path='/AdminScreen/bills/editTemplate/:id' element={<EditBillTemplate />}></Route>
										<Route path='/AdminScreen/bills/confirmPayment/:id' element={<ConfirmPayment />}></Route>
										<Route path='/AdminScreen/bills/bill/:id' element={<BillDetail />}></Route>
										<Route path='/AdminScreen/bills/bill/:id/:tab' element={<BillDetail />}></Route>
										<Route path='/AdminScreen/contracts' element={<ContractList />}></Route>
										<Route path='/AdminScreen/contracts/search' element={<ContractList />}></Route>
										<Route path='/AdminScreen/contracts/contract/:id' element={<ContractDetail />}></Route>
										<Route path='/AdminScreen/contracts/contract/:id/:tab' element={<ContractDetail />}></Route>
										<Route path='/AdminScreen/newContract' element={<AddContract />}></Route>
										<Route path='/AdminScreen/contracts/editContract/:id' element={<EditContract />}></Route>
										<Route path='/AdminScreen/contracts/contract/:id/newDebt' element={<AddDebt />}></Route>
										<Route path='/AdminScreen/contracts/newContractPayment' element={<ContractPaymentEditor />}></Route>
										<Route path='/AdminScreen/contracts/contract/:id/newContractPayment' element={<ContractPaymentEditor />}></Route>
										<Route path='/AdminScreen/contracts/contract/:contractId/debts/:id/edit' element={<EditDebt />}></Route>
										<Route path='/AdminScreen/interests' element={<InterestList />}></Route>
										<Route path='/AdminScreen/interests/interest/:id' element={<InterestInfo />}></Route>
										<Route path='/AdminScreen/newInterest' element={<AddInterest />}></Route>
										<Route path='/AdminScreen/interests/editinterest/:id' element={<InterestEditRedirect />}></Route>
										<Route path='/AdminScreen/interests/editInterest/:id' element={<EditInterest />}></Route>
										<Route path='/AdminScreen/assets' element={<AssetList />}></Route>
										<Route path='/AdminScreen/assets/search' element={<AssetList />}></Route>
										<Route path='/AdminScreen/assets/importar-gastos' element={<AdminRoute><ImportBills /></AdminRoute>}></Route>
										<Route path='/AdminScreen/assets/asset/:id' element={<AssetDetails />}></Route>
										<Route path='/AdminScreen/assets/asset/:id/:tab' element={<AssetDetails />}></Route>
										<Route path='/AdminScreen/newAsset' element={<AddAsset />}></Route>
										<Route path='/AdminScreen/assets/editAsset/:id' element={<EditAsset />}></Route>
										<Route path='/AdminScreen/assets/asset/:id/newBill' element={<NewBillsNew />}></Route>
										<Route path='/AdminScreen/assets/asset/:id/newContract' element={<AddContract />}></Route>
										<Route path='/AdminScreen/assets/asset/:assetId/contracts/:id/edit' element={<EditContract />}></Route>
										<Route path='/AdminScreen/assetSales' element={<AssetSalesList />}></Route>
										<Route path='/AdminScreen/assetSales/search' element={<AssetSalesList />}></Route>
										<Route path='/AdminScreen/assetSales/assetSale/:id' element={<AssetSaleDetail />}></Route>
										<Route path='/AdminScreen/assetSales/assetSale/:id/:tab' element={<AssetSaleDetail />}></Route>
										<Route path='/AdminScreen/assets/asset/:id/assetSale' element={<AddAssetSale />}></Route>
										<Route path='/AdminScreen/newAssetSale' element={<NewAssetSale />}></Route>
										<Route path='/AdminScreen/assetSales/assetSale/:id/newDebt' element={<AddDebt />}></Route>
										<Route path='/AdminScreen/assetSales/assetSale/:assetSaleId/debts/:id/edit' element={<EditDebt />}></Route>
										<Route path='/AdminScreen/assetSales/editAssetSale/:id' element={<EditAssetSale />}></Route>

										<Route
											path='/AdminScreen/AdminUsers'
											element={<AdminRoute><AdminScreen3 /></AdminRoute>}
										></Route>



										{getSettingsRoutes()}
										<Route
											path='/AdminScreen/contractTemplates/new'
											element={<ContractTemplateSetup />}
										></Route>
									</Route>
									<Route
										path='/contractTemplateEditor/:id'
										element={<ContractTemplateEditor />}
									></Route>

								</Routes>
							</main>
						</div>
						<AdminFooter></AdminFooter>
						{/* </BrowserRouter> */}
					</CustomBrowserRouter>
				</>
			)}
			<ToastHost placement='br' max={4} />
		</>
	);
}

export default App;
